import { createRoot } from 'react-dom/client';
import { AtlasWidget } from './AtlasWidget';
import type { WidgetConfig } from './types';
import { DEFAULT_KEYCLOAK_CLIENT_ID, DEFAULT_KEYCLOAK_REALM, DEFAULT_KEYCLOAK_URL } from '../constants';

// Default Keycloak configuration (compiled into widget from env vars)


/**
 * Initialize the Atlas Widget from a DOM element
 * Supported data attributes:
 * - data-position: Button position (floating mode) or null for inline mode
 * - data-overlay-position: Overlay/modal position (corners or sidebars)
 * All other config comes from build-time environment variables
 */
function initAtlasWidget(): void {
    const widgetElement = document.getElementById('atlas-widget');

    if (!widgetElement) {
        // Element not found - wait for it to appear (e.g., React hasn't rendered it yet)
        waitForElement('atlas-widget', initAtlasWidget, 10000); // 10 second timeout
        return;
    }

    // Prevent double initialization
    if ((widgetElement as any).__atlasWidgetInitialized) {
        return;
    }
    (widgetElement as any).__atlasWidgetInitialized = true;

    // Read position from data attribute (only supported data attribute)
    // If not provided, position will be null (inline mode)
    const position = widgetElement.dataset.position
        ? (widgetElement.dataset.position as WidgetConfig['position'])
        : null;

    // Read overlay position from data attribute
    // If not provided, default to position (or bottom-right if position is null)
    const overlayPosition = widgetElement.dataset.overlayPosition
        ? (widgetElement.dataset.overlayPosition as WidgetConfig['overlayPosition'])
        : (position || 'bottom-right');

    const applicationMetadata = widgetElement.dataset.metadata;

    // Use default configuration from environment variables (compiled into widget)
    const apiUrl = import.meta.env.VITE_ATLAS_API_URL;

    const keycloakUrl = DEFAULT_KEYCLOAK_URL;

    const config: WidgetConfig = {
        apiUrl,
        keycloakUrl,
        keycloakRealm: DEFAULT_KEYCLOAK_REALM,
        keycloakClientId: DEFAULT_KEYCLOAK_CLIENT_ID,
        position,
        overlayPosition,
        applicationMetadata
    };

    // If position is null, render inline at the element's location
    // Otherwise, render as a fixed floating widget
    if (position === null) {
        // Inline mode: render directly into the widget element
        const root = createRoot(widgetElement);
        root.render(<AtlasWidget config={config} />);
        console.log('[Atlas Widget] Initialized successfully in inline mode');
    } else {
        // Fixed position mode: create a fixed container
        const widgetRoot = document.createElement('div');
        widgetRoot.id = 'atlas-widget-root';
        widgetRoot.style.cssText = 'position: fixed; z-index: 999999; pointer-events: none; inset: 0;';
        document.body.appendChild(widgetRoot);

        // Create React root and render widget
        const root = createRoot(widgetRoot);
        root.render(<AtlasWidget config={config} />);
        console.log('[Atlas Widget] Initialized successfully', { position });
    }
}

/**
 * Wait for an element with the given ID to appear in the DOM
 * Uses MutationObserver for efficient detection, with polling fallback
 */
function waitForElement(
    elementId: string,
    callback: () => void,
    timeout: number = 10000
): void {
    const startTime = Date.now();
    let observer: MutationObserver | null = null;
    let pollInterval: number | null = null;

    const checkElement = (): boolean => {
        const element = document.getElementById(elementId);
        if (element) {
            // Clean up
            if (observer) {
                observer.disconnect();
            }
            if (pollInterval !== null) {
                clearInterval(pollInterval);
            }
            callback();
            return true;
        }

        // Check timeout
        if (Date.now() - startTime > timeout) {
            console.warn(
                `[Atlas Widget] Element with id="${elementId}" not found after ${timeout}ms. Widget will not initialize.`
            );
            if (observer) {
                observer.disconnect();
            }
            if (pollInterval !== null) {
                clearInterval(pollInterval);
            }
            return true;
        }

        return false;
    };

    // Try MutationObserver first (more efficient)
    if (typeof MutationObserver !== 'undefined') {
        observer = new MutationObserver(() => {
            if (checkElement()) {
                return;
            }
        });

        observer.observe(document.body, {
            childList: true,
            subtree: true,
        });
    }

    // Fallback: poll every 100ms
    pollInterval = window.setInterval(() => {
        checkElement();
    }, 100);

    // Also check immediately
    checkElement();
}

// Auto-initialize when DOM is ready
if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAtlasWidget);
} else {
    // Use setTimeout to ensure the script runs after other scripts
    setTimeout(initAtlasWidget, 0);
}

// Export for manual initialization if needed
(window as any).AtlasWidget = {
    init: initAtlasWidget,
};
